import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { KeyRound, Pencil } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { AccountBand, AccountSections } from "@/components/account-details";
import { AvatarPicker } from "@/components/avatar-picker";
import { EditProfileDialog } from "@/components/edit-profile-dialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Page, PageHeader } from "@/components/ui/page";
import { errorMessage } from "@/services/api";
import { profileQuery, useUpdateProfile } from "@/services/queries";
import { useTranslation } from "@/store/locale";

/**
 * The account as it stands, read first; the three things a person may do to it
 * sit on top.
 *
 * Editing opens the sheet the dashboard card opens. It used to unfold a second
 * form in place, and two forms over one record drift: this one asked for the
 * name in three boxes and could edit the email, that one asks for it as the
 * single line a person actually has and carries the picture, the note and the
 * numbers. One record, one form.
 */
function ProfilePage() {
  const t = useTranslation();
  const { data: profile } = useQuery(profileQuery());
  const update = useUpdateProfile();
  const [editing, setEditing] = useState(false);

  if (!profile) return null;
  const name = profile.full_name || profile.user_name;

  const setPicture = async (avatar: string) => {
    try {
      await update.mutateAsync({ avatar });
      toast.success(t("shell.pictureUpdated"));
    } catch (error) {
      toast.error(errorMessage(error, t("shell.pictureFailed")));
    }
  };

  return (
    <Page>
      <PageHeader title={t("page.profile")} description={t("profile.subtitle")} />

      {/* One card, top to bottom: who they are, then everything on the account. */}
      <Card>
        <AccountBand
          account={profile}
          avatar={
            // The same picker the employee record uses, so a picture is
            // changed the one way wherever it is changed — including from the
            // camera, which this screen could not do at all.
            <AvatarPicker
              src={profile.avatar}
              name={name}
              busy={update.isPending}
              onChange={setPicture}
              onRemove={() =>
                update.mutate(
                  { avatar: "" },
                  { onSuccess: () => toast.success(t("profile.pictureRemoved")) },
                )
              }
            />
          }
          actions={
            <>
              <Button variant="outline" onClick={() => setEditing(true)}>
                <Pencil /> {t("profile.editProfile")}
              </Button>
              <Button asChild>
                <Link to="/profile/password">
                  <KeyRound /> {t("shell.changePassword")}
                </Link>
              </Button>
            </>
          }
        />

        <AccountSections account={profile} />
      </Card>

      {/* The sheet the dashboard card opens, not a second form that happens to
          edit the same account. Two forms over one record drift: this page
          asked for the name in three boxes and could edit the email, that one
          asks for it as the single line a person actually has and carries the
          picture, the note and the numbers. Mounted only while open, so each
          visit starts from the record rather than from whatever was half-typed
          last time. */}
      {editing ? (
        <EditProfileDialog profile={profile} open onClose={() => setEditing(false)} />
      ) : null}
    </Page>
  );
}

export default ProfilePage;
